// The list, the table and the status lights: words and numbers in rows, at a
// reading size that grows a little with the tile, then shows more rows. They
// were in data.tsx, which the fix session holds until its push; the ones
// there go once it lands (the table keeps that session's change: a source's
// note shows even with no rows).

import { formatNumber } from "../../shared/format";
import type { Stream, StreamItem } from "../../shared/types";
import { cx } from "../lib/util";
import { Linked } from "../lib/Linked";
import { useSize } from "../charts/charts";
import type { WP } from "./data";
import { readingType } from "./plans";
import { dateCell, rowCurrency, statusText, statusTone, viewColumns, type ViewColumn } from "./tableView";
import { Ranked, rankedColumns, Split } from "./breakdown";

function Value({ v, format }: { v: StreamItem["value"]; format: Stream["format"] }) {
  if (v === null || v === undefined || v === "") return null;
  return <span className="rw-value">{typeof v === "number" ? formatNumber(v, format) : v}</span>;
}

const dotOf = (status: StreamItem["status"]) => (status === "ok" ? "live" : status === "warn" ? "warn" : status === "bad" ? "bad" : "idle");

/** A ranked list: each row's share of the biggest drawn behind it, its rank or its light, and its value. */
export function List(p: WP) {
  // parts of a whole, drawn as a split; a list whose rows carry more numbers, ranked with them (breakdown.tsx)
  if (p.item.props.look === "split") return <Split {...p} />;
  if (p.stream && rankedColumns(p.stream)) return <Ranked {...p} />;
  return <Rows {...p} />;
}

function Rows({ item, stream }: WP) {
  const s = stream!;
  const [ref, size] = useSize<HTMLDivElement>();
  const items = (s.items ?? []).slice(0, Number(item.props.max ?? 50) || 50);
  const max = Math.max(0, ...items.map((i) => (typeof i.value === "number" ? Math.abs(i.value) : 0)));
  const bars = item.props.bars !== false && max > 0;
  if (!items.length)
    return (
      <div className="empty">
        <span className="empty-title">The list is empty</span>
      </div>
    );
  return (
    <div className="rw-list" ref={ref} style={{ fontSize: readingType(size.w, 13, 17) }} data-scroll>
      {items.map((it, i) => {
        const w = bars && typeof it.value === "number" ? (Math.abs(it.value) / max) * 100 : 0;
        // a row that is a link already opens as one; in any other, a web address in its words opens with a click
        const words = (t: string) => (it.href ? t : <Linked text={t} />);
        const inner = (
          <>
            {bars && <span className="rw-bar" style={{ width: `${w}%` }} />}
            {item.props.numbered ? <span className="rw-rank">{i + 1}</span> : it.status ? <span className={cx("dot", `dot-${dotOf(it.status)}`)} /> : null}
            <span className="rw-label">
              <span className="rw-title">{words(it.label)}</span>
              {it.sub && <span className={cx("rw-sub", it.status === "bad" && "is-bad", it.status === "ok" && it.sub.startsWith("+") && "is-good")}>{words(it.sub)}</span>}
            </span>
            <Value v={it.value} format={s.format} />
          </>
        );
        return it.href ? (
          <a key={i} className="rw" href={it.href} target="_blank" rel="noreferrer noopener" data-interactive>
            {inner}
          </a>
        ) : (
          <div key={i} className="rw">
            {inner}
          </div>
        );
      })}
    </div>
  );
}

/** Rows and columns, straight from the data: numbers on the right, and what is not shown said under it. */
export function Table({ item, stream }: WP) {
  const s = stream!;
  const [ref, size] = useSize<HTMLDivElement>();
  const rows: Record<string, unknown>[] = s.rows ?? (s.items ?? []).map((i) => ({ label: i.label, value: i.value, ...(i.sub ? { detail: i.sub } : {}) }));
  const cols = s.columns ?? (rows[0] ? Object.keys(rows[0]) : []);
  const max = Number(item.props.max ?? 50) || 50;
  // with no rows, what the source said it left out is said all the same
  if (!rows.length)
    return (
      <div className="empty">
        <span className="empty-title">No rows yet</span>
        {s.note && <span className="empty-sub">{s.note}</span>}
      </div>
    );
  // each column read as a person would: dates as dates, money in its currency, a status by its tone (tableView.ts)
  const view = viewColumns(cols, rows);
  // what is not shown, said: rows past what the tile shows, and what the source itself left out
  const cut = [rows.length > max ? `Showing ${max} of ${rows.length} rows.` : "", s.note ?? ""].filter(Boolean).join(" ");
  const cell = (c: ViewColumn, r: Record<string, unknown>) => {
    const v = r[c.key];
    if (v === null || v === undefined || v === "") return "";
    if (c.kind === "date" && typeof v === "string") return dateCell(v);
    if (c.kind === "money" && typeof v === "number") {
      const cur = rowCurrency(r);
      return formatNumber(v, cur ? { style: "currency", currency: cur, decimals: 2 } : null);
    }
    if (c.kind === "status" && typeof v === "string") {
      const tone = statusTone(v);
      return <span className={cx("tb-tag", tone && `is-${tone}`)}>{statusText(v)}</span>;
    }
    if (c.kind === "flag") return v === true ? "Yes" : "No";
    return typeof v === "number" ? formatNumber(v, null) : <Linked text={String(v)} />;
  };
  return (
    <div className="tb" ref={ref} style={{ fontSize: readingType(size.w, 12.5, 15.5) }} data-scroll>
      <table>
        <thead>
          <tr>
            {view.map((c) => (
              <th key={c.key} className={c.kind === "number" || c.kind === "money" ? "num" : undefined}>
                {c.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, max).map((r, ri) => (
            <tr key={ri}>
              {view.map((c) => (
                <td key={c.key} className={cx(c.kind === "number" || c.kind === "money" ? "num" : undefined, c.kind === "date" && "tb-date")}>
                  {cell(c, r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {cut && <p className="tb-note">{cut}</p>}
    </div>
  );
}

/** Green, amber or red for the things that must stay up: what is wrong first, in one line, then each light. */
export function Status({ stream }: WP) {
  const s = stream!;
  const [ref, size] = useSize<HTMLDivElement>();
  const items = s.items ?? [];
  const bad = items.filter((i) => i.status === "bad").length;
  const warn = items.filter((i) => i.status === "warn").length;
  const summary = !items.length ? "Nothing to check yet" : bad ? `${bad} of ${items.length} down` : warn ? `${warn} slow` : `All ${items.length} up`;
  return (
    <div className="sl" ref={ref} style={{ fontSize: readingType(size.w, 13, 17) }}>
      <div className={cx("sl-sum", bad ? "is-bad" : warn ? "is-warn" : "is-ok")}>
        <span className="sl-pulse" />
        {summary}
      </div>
      <div className="sl-rows" data-scroll>
        {items.map((it, i) => (
          <div key={i} className={cx("sl-row", it.status && `is-${it.status}`)}>
            <span className={cx("dot", `dot-${dotOf(it.status)}`)} />
            <span className="rw-label">
              <span className="rw-title">
                <Linked text={it.label} />
              </span>
              {it.sub && (
                <span className="rw-sub">
                  <Linked text={it.sub} />
                </span>
              )}
            </span>
            <Value v={it.value} format={s.format} />
          </div>
        ))}
      </div>
    </div>
  );
}
